<template>
  <div class="reload-config-dialog">
    <SDialog :open="modalVisibility" @update:open="onOpenChange">
      <SDialogContent
        class="reload-config-dialog !w-[650px] !max-w-[650px]"
        data-cy="reload-config-modal"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("config.reload-config") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="d-flex fl-col fl-j-c fl-a-c progress-container">
          <p>{{ getLoadingText }}</p>
          <div class="progress-bar">
            <el-progress :percentage="progressValue" class="progress" />
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import POSConfigModule from "@/store/modules/pos-config";
import { IPOSConfig } from "@/types/db/pos-config";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import NotificationService from "@/services/notification.service";
import { ModalStatus } from "@/constants/modal";
import { LogLevel, LogService } from "@/services/logging";
import { getSalesConfigurationById } from "@/api/gql/sales-configurations";
import { toLocalPOSConfig, loadConfigData } from "@/utils/helpers/pos-config";
import { proceedFetchAndStoreGroupPicture } from "@/utils/helpers/product-category";
import { hasSalesStarted } from "@/utils/vue-helpers/cash-float";
import BookingModule from "@/store/modules/booking";
import ModalModule from "@/store/modules/modal";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "ReloadConfiguration",
  components: {
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    console.log("hi from reloadConfig");
    const { t } = useI18n({});
    const modalVisibility = dialogVisibleForStatus(
      ModalStatus.ReloadConfiguration
    );
    const abortController = ref<AbortController | null>(null);

    const getLoadingText = computed(() => {
      return POSConfigModule.getLoadingText;
    });

    const progressValue = computed(() => {
      let pcsProgress = POSConfigModule.pcsLoaded;
      let timeslotsProgress = POSConfigModule.timeslotsLoaded;
      if (pcsProgress > 50 || timeslotsProgress) {
        // Cap PCs progress at 50 once timeslots start loading
        pcsProgress = 50;
      }
      if (timeslotsProgress > 35) {
        // Cap timeslots progress at 35
        timeslotsProgress = 35;
      }
      // extraProgress covers the remaining 5% (seating plans, promotions, offers, locations)
      return (
        10 + pcsProgress + timeslotsProgress + POSConfigModule.extraProgress
      );
    });

    async function handleBeforeClose(done: () => void) {
      LogService.info("User initiated config reload cancellation");
      const confirmed = await showConfirm({
        title: t("common.warning"),
        message: t("config.abort-reload-warning"),
        variant: "destructive",
        confirmText: t("common.yes"),
        cancelText: t("common.no"),
      });

      if (confirmed) {
        LogService.info("User confirmed to abort config reload");
        abortController.value?.abort();
        done();
      } else {
        LogService.info("User cancelled aborting config reload");
      }
    }

    // SDialog emits `update:open(false)` for close paths (X button); proxy
    // through handleBeforeClose to preserve the el-dialog `:before-close`
    // semantics, close only after the abort confirmation resolves. Skip the
    // open path: dialogVisibleForStatus's setter ignores its argument and
    // closes the dialog on any assignment, so we must not write on open.
    function onOpenChange(open: boolean) {
      if (open) {
        return;
      }
      handleBeforeClose(() => {
        modalVisibility.value = false;
      });
    }

    function resetProgress() {
      POSConfigModule.setPCsLoaded(0);
      POSConfigModule.setTimeslotsLoaded(0);
      POSConfigModule.setExtraProgress(0);
    }

    // reload the current selected config
    async function reloadConfig() {
      const controller = new AbortController();
      abortController.value = controller;
      const signal = controller.signal;

      function throwIfAborted() {
        if (signal.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
      }

      // to also refresh the group info
      try {
        await POSConfigModule.fetchGroupInfo();
      } catch (error) {
        LogService.info(
          `Error while fetching Group Info`,
          error,
          LogLevel.Error
        );
      }

      try {
        throwIfAborted();

        // Pulling group pictures and storing them in Dexie
        await proceedFetchAndStoreGroupPicture(signal);

        throwIfAborted();

        // refresh the selected config data from backend as well
        const selectedConfig = POSConfigModule.selectedConfig;
        if (!selectedConfig) {
          // If no selected config then we don't reload
          LogService.info(
            `There is no selected config to be reloaded`,
            undefined,
            LogLevel.Error
          );
          return;
        }

        throwIfAborted();

        const refreshedConfig = await getSalesConfigurationById(
          selectedConfig.id
        );

        throwIfAborted();

        const convertedConfig = toLocalPOSConfig(refreshedConfig[0]);

        POSConfigModule.SET_SELECTED_CONFIG({
          ...convertedConfig,
        } as IPOSConfig);
        // clear the selected grid
        POSConfigModule.selectGrid(null);

        const success = await loadConfigData(convertedConfig, signal);

        if (signal.aborted) {
          LogService.info("Config reload aborted by user");
          resetProgress();
          if (!BookingModule.selectedBooking) {
            BookingModule.createNewBooking();
          }
          return;
        }

        if (success) {
          setTimeout(async () => {
            if (signal.aborted) return;
            // It is sure that every data from remote to start selling is now pulled and stored in local db
            // lets create a new booking if sales has already started
            if (hasSalesStarted.value) {
              console.log("******** calling BookingModule.createNewBooking");
              BookingModule.createNewBooking();
            }
            hideDialog();
            resetProgress();
          }, 500);
        }
      } catch (error) {
        if (signal.aborted) {
          // User aborted the reload — reset progress and exit silently
          LogService.info("Config reload aborted by user");
          resetProgress();
          return;
        }
        hideDialog();
        LogService.info(
          `Error while refetching selected config`,
          error,
          LogLevel.Error
        );
        NotificationService.showGeneralResponseError();
      }
    }

    return {
      t,
      modalVisibility,
      progressValue,
      reloadConfig,
      getLoadingText,
      onOpenChange,
    };
  },

  async mounted() {
    // refresh the selected config data from backend as well
    const selectedConfig = POSConfigModule.selectedConfig;
    if (!selectedConfig) {
      // If no selected config then we don't reload
      LogService.info(
        `There is no selected config to be reloaded`,
        undefined,
        LogLevel.Error
      );
      hideDialog();
    } else {
      this.reloadConfig();
    }
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.reload-config-dialog {
  .progress-container {
    height: 200px;
    color: $color-black-2;
    font-size: 14px;
    line-height: 17px;
    font-family: Arial, sans-serif, Arial;
    font-weight: 400;
    margin-top: -30px;
  }
  .progress-bar {
    padding-top: 20px;
    padding-left: 40px;
    width: 400px;
  }
}
</style>
